"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { ArrowRight, Phone, Globe, X, User, Mail } from "lucide-react";
import { ROUTES } from "@/lib/routes";
import {
  useCreateLeadMutation,
  type LeadCategory,
} from "@/redux/features/lead/leadApi";
import { toast } from "sonner";
import { trackCallClicked } from "@/lib/analytics";

type PopupVariant = {
  id: number;
  type: "booking" | "email";
  image: string;
  title: string; // headline
  subtitle: string;
  placeholder?: string;
  buttonLabel?: string;
  category?: LeadCategory;
};

const popupVariants: PopupVariant[] = [
  {
    id: 0,
    type: "booking",
    image: "/images/alex-popup.png",
    title: "Find Your Path Forward",
    subtitle:
      "You don't have to handle it alone. Take a simple step toward clarity and peace of mind with expert professional guidance.",
  },
  {
    id: 1,
    type: "email",
    image: "/images/blogs/assets/1 (1).png",
    title: "Control the Anxiety",
    subtitle: "Get the free 3-3-3 Grounding Guide sent straight to your inbox.",
    placeholder: "Enter your email address & receive your guide...",
    buttonLabel: "Get Your Free Guide",
    category: "grounding_guide_333",
  },
  {
    id: 2,
    type: "email",
    image: "/images/blogs/assets/2.png",
    title: "Find Your Peace & Reset",
    subtitle:
      "Start your healing journey with the pause & reset blueprint sent to your inbox",
    placeholder: "Enter your email address & receive your blueprint...",
    buttonLabel: "Get Your Free Blueprint",
    category: "pause_reset_blueprint",
  },
  {
    id: 3,
    type: "email",
    image: "/images/blogs/assets/3.png",
    title: "Stay Calm, Avoid Burnout",
    subtitle:
      "Get the free anti burnout weekly planner delivered straight to your inbox",
    placeholder: "Enter your email address & receive your planner...",
    buttonLabel: "Get Your Free Planner",
    category: "anti_burnout_weekly_planner",
  },
];

const BookingPopup = () => {
  const [activeVariant, setActiveVariant] = useState<PopupVariant | null>(null);

  // Form states for email variants
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Lead API mutation
  const [createLead] = useCreateLeadMutation();

  useEffect(() => {
    // 1. Initialize random variant IDs for session if not already present
    let popupId: number;
    let ctaId: number;

    const popupIdStr = sessionStorage.getItem("blog-session-popup-id");
    const ctaIdStr = sessionStorage.getItem("blog-session-cta-id");

    if (popupIdStr !== null && ctaIdStr !== null) {
      popupId = parseInt(popupIdStr);
      ctaId = parseInt(ctaIdStr);
    } else {
      // Pick random popup from [0, 1, 2, 3]
      const ids = [0, 1, 2, 3];
      popupId = ids[Math.floor(Math.random() * ids.length)];

      if (popupId === 0) {
        // CTA is randomly [1, 2, 3]
        const ctaIds = [1, 2, 3];
        ctaId = ctaIds[Math.floor(Math.random() * ctaIds.length)];
      } else {
        ctaId = popupId;
      }

      sessionStorage.setItem("blog-session-popup-id", popupId.toString());
      sessionStorage.setItem("blog-session-cta-id", ctaId.toString());
    }

    // 2. Check if popup was dismissed
    const isDismissed =
      sessionStorage.getItem("blog-session-popup-dismissed") === "1";
    if (isDismissed) return;

    // 3. Scroll trigger logic
    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight <= 0) return;
      const scrollPercent = scrollTop / docHeight;

      if (scrollPercent >= 0.5) {
        const currentVariant =
          popupVariants.find((v) => v.id === popupId) || popupVariants[0];
        setActiveVariant(currentVariant);
        window.removeEventListener("scroll", handleScroll);
      }
    };

    // Check immediately in case they are already scrolled past 50%
    handleScroll();

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const dismiss = () => {
    sessionStorage.setItem("blog-session-popup-dismissed", "1");
    setActiveVariant(null);
    setName("");
    setEmail("");
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !email || isSubmitting || !activeVariant?.category) return;

    setIsSubmitting(true);
    try {
      await createLead({
        fullName: name,
        email,
        source: "POPUP",
        category: activeVariant?.category,
      }).unwrap();
      setName("");
      setEmail("");
      toast.success("Check your inbox!");

      // Auto-close popup after short delay
      setTimeout(() => {
        dismiss();
      }, 1500);
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!activeVariant) return null;

  return (
    <div
      className="fixed inset-0 z-[999] flex items-center justify-center overflow-y-auto p-4"
      role="dialog"
      aria-modal="true"
      aria-label={activeVariant.title}
      data-booking-popup-open="true"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={dismiss}
      />

      {/* Panel */}
      <div className="relative z-10 my-auto flex w-full max-w-5xl overflow-hidden rounded-2xl shadow-2xl">
        {/* Left – photo */}
        <div className="relative hidden w-[38%] shrink-0 bg-gray-950 lg:block">
          <Image
            src={activeVariant.image}
            alt={activeVariant.title}
            fill
            sizes="800px"
            className="object-cover object-top"
            priority
          />
        </div>

        {/* Right – content */}
        <div className="relative flex flex-1 flex-col overflow-hidden bg-gradient-to-b from-[#034FB6] to-[#013B89] px-6 py-10 text-white sm:py-16 lg:px-9 lg:py-20">
          {/* Watermark */}
          <h2
            className="pointer-events-none absolute -bottom-3 left-1/2 -translate-x-1/2 select-none whitespace-nowrap text-5xl leading-none text-white/10 sm:text-6xl md:-bottom-6 md:text-7xl lg:text-[90px]"
            aria-hidden
          >
            <span className="relative inline-block -translate-y-2 font-sorts-mill-goudy font-light tracking-tighter">
              Alexander
            </span>{" "}
            <span className="font-virtual font-normal sm:text-5xl md:text-6xl lg:text-8xl">
              Rodriguez
            </span>
          </h2>

          {/* Close button */}
          <button
            onClick={dismiss}
            className="absolute right-4 top-4 z-20 rounded-full p-1.5 text-white/70 transition-colors hover:bg-white/10 hover:text-white"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>

          {/* Title */}
          <h2 className="font-virtual text-[42px] leading-tight text-secondary sm:text-[56px] md:text-[70px] lg:text-[82px]">
            {activeVariant.title}
          </h2>

          {/* Subtitle */}
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-white/90 sm:text-base">
            {activeVariant.subtitle}
          </p>

          {activeVariant.type === "booking" ? (
            <>
              {/* Bullet points */}
              <ul className="mt-3 max-w-xl space-y-1.5 pl-3 text-sm text-white/90 sm:text-base">
                {[
                  "Tailored Support: Strategies customized for your unique mental health goals.",
                  "Practical Tools: Proven methods to handle daily pressure and build resilience.",
                  "Trusted Expertise: Clinical and coaching insights to help you thrive.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-white" />
                    {item}
                  </li>
                ))}
              </ul>

              {/* CTA text */}
              <p className="mt-4 text-sm text-white/80 sm:text-base">
                Click below to check availability and schedule your consultation
              </p>

              {/* Book Now button */}
              <div className="z-10 mb-3 mt-6 sm:mt-8">
                <Link
                  href={ROUTES.booking}
                  onClick={dismiss}
                  className="group relative inline-flex items-center gap-2 overflow-hidden rounded-lg bg-white px-6 py-2.5 text-sm font-semibold text-primary transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_0_40px_rgba(2,79,183,0.35)] sm:gap-2.5 sm:rounded-xl sm:px-8 sm:py-3 sm:text-base"
                  aria-label="Book Now"
                >
                  <span className="relative z-10 whitespace-nowrap">
                    Book Now
                  </span>
                  <ArrowRight
                    className="relative z-10 h-4 w-4 shrink-0 transition-transform duration-300 group-hover:translate-x-1 sm:h-5 sm:w-5"
                    aria-hidden
                  />
                  <span className="absolute -inset-2 -translate-x-full skew-x-12 bg-primary/40 transition-transform duration-700 group-hover:translate-x-full" />
                </Link>
              </div>
            </>
          ) : (
            /* Email Subscription Form */
            <form
              onSubmit={handleEmailSubmit}
              className="z-10 mt-6 flex max-w-md flex-col gap-3.5"
            >
              {/* Full Name Input */}
              <div className="flex items-center gap-2.5 rounded-full bg-white p-1.5 shadow-md">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#034FB6]">
                  <User className="h-4.5 w-4.5 text-white" />
                </div>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your full name..."
                  className="w-full bg-transparent pr-4 font-onest text-sm text-gray-700 placeholder-gray-400 outline-none"
                  required
                />
              </div>

              {/* Email Address Input */}
              <div className="flex items-center gap-2.5 rounded-full bg-white p-1.5 shadow-md">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#034FB6]">
                  <Mail className="h-4.5 w-4.5 text-white" />
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={activeVariant.placeholder}
                  className="w-full bg-transparent pr-4 font-onest text-sm text-gray-700 placeholder-gray-400 outline-none"
                  required
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="mt-1 w-full rounded-full bg-[#c5ab5d] py-2.5 font-onest text-sm font-semibold text-white shadow-md transition-all duration-300 hover:scale-[1.02] hover:bg-[#b0964d] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSubmitting ? "Submitting..." : activeVariant.buttonLabel}
              </button>
            </form>
          )}

          {/* Service tags */}
          <div className="mt-5 flex items-center gap-5 text-xs font-medium sm:text-sm">
            <span>Counselling</span>
            <span>Allied Health</span>
            <span>Life Coach</span>
          </div>

          {/* Contact */}
          <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs text-white/85 sm:text-sm">
            <a
              href="tel:+611300467513"
              className="flex items-center gap-2 transition-colors duration-200 hover:text-white"
              onClick={() => trackCallClicked({ location: "booking_popup" })}
            >
              <span className="flex shrink-0 items-center justify-center rounded-full bg-white p-1">
                <Phone
                  fill="currentColor"
                  className="h-3 w-3 text-primary sm:h-3.5 sm:w-3.5"
                />
              </span>
              <span className="hover:underline">1300 467 513</span>
            </a>
            <a
              href="https://www.alexrodriguez.com.au"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 transition-colors duration-200 hover:text-white"
            >
              <span className="flex shrink-0 items-center justify-center rounded-full bg-white p-1">
                <Globe className="h-3 w-3 text-primary sm:h-3.5 sm:w-3.5" />
              </span>
              <span className="hover:underline">www.alexrodriguez.com.au</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingPopup;
